import { FACE_HEIGHT_MAX } from "../terrain/faces.ts";
import { playersAbove } from "./band.ts";
import { tileRefToPacked } from "./packed-tile.ts";
import { changeTilesets, type TilesetEntry, type TilesetFrames } from "./tileset-change.ts";
import { addMapTo, removeMapFrom, renameMapIn } from "./map-commands.ts";
import { applyLayoutCommand, invertLayoutCommand, isLayoutCommand, type LayoutCommand } from "./layout-commands.ts";
import { assertInstanceFootprint } from "./footprint-cells.ts";
import { IGNORED_ISSUES_MAX, readIssueIgnores } from "./issue-ignores.ts";
import { sameJson } from "./same-json.ts";
import { validateDocument } from "./layout-rules.ts";
import { editZoneCells, fillZoneCells, type ZoneCellChange } from "./zones.ts";
import { applyDoorCommand, invertDoorCommand, isDoorCommand, type DoorCommand } from "./door-commands.ts";
import { assertUniqueDoorIds, fillDoorCells } from "./doors.ts";
import { COLLISION_CELLS_PER_TILE, type EntityInstance, type InstanceFootprint, type IssueIgnores, type JsonValue, type LayerDocument, type LayerGroup, type MapDocument, type PropBehaviour, type PropInstance, type TileRef, type TilesetRef, type WorldDocument } from "./types.ts";

/**
 * Editor commands over authoring documents. Every command is plain data carrying enough to invert itself
 * exactly, and `applyCommand` is a pure function that returns a new document, copying only the layer it
 * touches. Preconditions (recorded `before` values, carried items) are checked on apply and violations throw,
 * which is what makes undo exact rather than best-effort.
 */

/**
 * One corner of a terrain stroke: the wang colour it had and the colour it gets.
 */
export interface CellChange {
  readonly index: number;
  readonly before: number;
  readonly after: number;
}

/**
 * One cell of a tile stroke as `TileRef`s, null meaning empty. Commands never carry the packed form; it is
 * produced inside `applyCommand` against the world's tilesets.
 */
export interface TileCellChange {
  readonly index: number;
  readonly before: TileRef | null;
  readonly after: TileRef | null;
}

/**
 * Position and fields of an entity, the part `updateEntity` changes.
 */
export interface EntityState {
  readonly x: number;
  readonly y: number;
  readonly fields: { readonly [key: string]: JsonValue };
}

/**
 * All editor commands. One brush stroke is one `paintCorners`, `setTiles`, `paintCollision`, `paintZone` or
 * `paintDoor` command; a zone stroke carries each cell's type id before and after, never its byte, since a zone layer's
 * table is renumbered to stay canonical (ADR-054's amendment of 2026-09-27). One
 * fill (P1.6-T40) is one `fillCells`: a flood region shares one value before and one after, so it carries its
 * cells as one typed array of indices into the layer's grid (the corners of a terrain layer, the cells of a
 * tile, collision, zone or door layer) with the two values, a byte, a tile, a zone type id or a door id, never a record a cell; its inverse swaps
 * the values and shares the indices, which no one writes.
 * `changeTilesets` is the one command over the whole world rather than one map (ADR-037 amendment): it
 * removes and inserts entries of the tileset list, removed indices counted before the change and inserted
 * ones after it, each with the frames a partial entry holds, sets the frames of entries that stay through its
 * optional third list `frames` (ADR-129 decision 3.4), and renumbers every packed tile cell; its inverse swaps the
 * two lists and each frames entry's `before` and `after`. `compound` applies several commands in order as one
 * edit (ADR-066): each sees the document the one before it left, the whole list fails if any command in it
 * fails, and its inverse is the reversed list of their inverses, so one undo takes it back.
 * `setMapProperties` replaces a map's whole `properties` record (P1.6-T28), carrying the record before so its
 * inverse restores it exactly. `setIssueIgnores` replaces a map's record of ignored issues the same way
 * (ADR-070's amendment of 2026-10-05), an empty record leaving the map without the field; it is refused when
 * it would leave more than `IGNORED_ISSUES_MAX` and more than the map held. `setTerrainName` is, like `changeTilesets`, over the world: it sets or, with
 * `after` absent, removes the author's name for one terrain entry (ADR-038 amendment of 2026-09-26, decision
 * 4). `renameLayer` carries a terrain layer's `autoName` flag before and after when it changes it, each absent
 * when the layer carries none (decision 7). `addMap`, `removeMap` and `renameMap` are over the world too
 * (ADR-021's amendment "several maps"): a whole map inserted at or taken from its index in the world's list,
 * each the other's inverse, and a map's name changed alone; a map is taken out only once no seam names it. The
 * layout commands (`LayoutCommand`, ADR-109 decision 10) are over the world as well: a map's place, group and
 * draft mark, the grid, the groups, one seam or one crossing. The door commands (`DoorCommand`, ADR-122 decision
 * 2.1) are over one door layer: a stroke, a door added, removed or changed.
 */
export type Command =
  | LayoutCommand
  | DoorCommand
  | { readonly kind: "compound"; readonly commands: readonly Command[] }
  | { readonly kind: "changeTilesets"; readonly remove: readonly TilesetEntry[]; readonly insert: readonly TilesetEntry[]; readonly frames?: readonly TilesetFrames[] }
  | { readonly kind: "setTerrainName"; readonly entry: string; readonly before?: string; readonly after?: string }
  | { readonly kind: "addMap"; readonly index: number; readonly map: MapDocument }
  | { readonly kind: "removeMap"; readonly index: number; readonly map: MapDocument }
  | { readonly kind: "renameMap"; readonly mapId: string; readonly before: string; readonly after: string }
  | { readonly kind: "paintCorners"; readonly mapId: string; readonly layerId: string; readonly cells: readonly CellChange[] }
  | { readonly kind: "paintCollision"; readonly mapId: string; readonly layerId: string; readonly cells: readonly CellChange[] }
  | { readonly kind: "paintZone"; readonly mapId: string; readonly layerId: string; readonly cells: readonly ZoneCellChange[] }
  | { readonly kind: "setTiles"; readonly mapId: string; readonly layerId: string; readonly cells: readonly TileCellChange[] }
  | { readonly kind: "fillCells"; readonly mapId: string; readonly layerId: string; readonly indices: Readonly<Uint32Array>; readonly before: FillCellValue; readonly after: FillCellValue }
  | { readonly kind: "addLayer"; readonly mapId: string; readonly index: number; readonly layer: LayerDocument; readonly group?: LayerGroup }
  | { readonly kind: "removeLayer"; readonly mapId: string; readonly index: number; readonly layer: LayerDocument; readonly group?: LayerGroup }
  | { readonly kind: "addGroup"; readonly mapId: string; readonly group: LayerGroup }
  | { readonly kind: "removeGroup"; readonly mapId: string; readonly group: LayerGroup }
  | { readonly kind: "renameGroup"; readonly mapId: string; readonly groupId: string; readonly before: string; readonly after: string }
  | { readonly kind: "setPlayersAbove"; readonly mapId: string; readonly before: number; readonly after: number }
  | { readonly kind: "setMapProperties"; readonly mapId: string; readonly before: { readonly [key: string]: JsonValue }; readonly after: { readonly [key: string]: JsonValue } }
  | { readonly kind: "setIssueIgnores"; readonly mapId: string; readonly before: IssueIgnores; readonly after: IssueIgnores }
  | { readonly kind: "setLayerGroup"; readonly mapId: string; readonly layerId: string; readonly before: string | undefined; readonly after: string | undefined }
  | {
      /**
       * Moves the layer at `from` so that it ends up at index `to` in the resulting order. Swapping `from` and
       * `to` is the exact inverse. `playersAbove` (ADR-047) carries the band before and after, since a drop at
       * the band's own index may keep a layer on either side; without it a pinned band keeps its index.
       * `layerId`, when carried, is the moved layer's, checked on apply, so a move never takes another layer
       * after a merge shifted the list (ADR-086 decision 8).
       */
      readonly kind: "moveLayer";
      readonly mapId: string;
      readonly from: number;
      readonly to: number;
      readonly layerId?: string;
      readonly playersAbove?: { readonly before: number; readonly after: number };
    }
  | { readonly kind: "addProp"; readonly mapId: string; readonly layerId: string; readonly index: number; readonly prop: PropInstance }
  | { readonly kind: "removeProp"; readonly mapId: string; readonly layerId: string; readonly index: number; readonly prop: PropInstance }
  | { readonly kind: "addEntity"; readonly mapId: string; readonly layerId: string; readonly index: number; readonly entity: EntityInstance }
  | { readonly kind: "removeEntity"; readonly mapId: string; readonly layerId: string; readonly index: number; readonly entity: EntityInstance }
  | {
      readonly kind: "updateEntity";
      readonly mapId: string;
      readonly layerId: string;
      readonly entityId: string;
      readonly before: EntityState;
      readonly after: EntityState;
    }
  | { readonly kind: "renameLayer"; readonly mapId: string; readonly layerId: string; readonly before: string; readonly after: string; readonly autoName?: AutoNameChange }
  | { readonly kind: "setLayerTerrain"; readonly mapId: string; readonly layerId: string; readonly before: LayerTerrain; readonly after: LayerTerrain }
  | {
      /**
       * Sets a terrain layer's face height (ADR-072 decision 6): `before` and `after` are each a height or
       * absent, absent being the terrain's own face height.
       */
      readonly kind: "setLayerFaceHeight";
      readonly mapId: string;
      readonly layerId: string;
      readonly before?: number;
      readonly after?: number;
    }
  | { readonly kind: "updateProp"; readonly mapId: string; readonly layerId: string; readonly propId: string; readonly before: PropState; readonly after: PropState };

/**
 * What a `fillCells` region holds before or after: a byte (a corner colour or a collision byte), a tile, a zone
 * type id or a door id, null for an empty tile, an unpainted zone cell or a cell holding no door.
 */
export type FillCellValue = number | TileRef | string | null;

/**
 * A terrain layer's `autoName` flag before and after a rename, each absent when the layer carries no flag.
 */
export interface AutoNameChange {
  readonly before?: boolean;
  readonly after?: boolean;
}

/**
 * A terrain layer's terrain and variant, the part `setLayerTerrain` changes, with the layer's name when the
 * change renames it and its face height, absent when the layer sets none (ADR-072 decision 6), so a change
 * that drops a height the new terrain cannot take undoes in one step with it.
 */
export interface LayerTerrain {
  readonly terrain: string;
  readonly variant: string;
  readonly name?: string;
  readonly faceHeight?: number;
}

/**
 * Frame, position, behaviour and own footprint of a prop, the part `updateProp` changes. `behaviour` and
 * `footprint` are absent rather than undefined on a prop that carries none, so a prop read from a save and
 * one an editor command wrote compare equal (ADR-053, ADR-040 amendment of 2026-10-04). A state never holds
 * both, and a change of frame never keeps the footprint it had. `keepPlacement` is present, and true, only on
 * a prop kept as placed (ADR-040 amendment of 2026-10-04), for the same reason.
 */
export interface PropState {
  readonly frame: TileRef;
  readonly x: number;
  readonly y: number;
  readonly flipX: boolean;
  readonly flipY: boolean;
  readonly sortPixels: number;
  readonly behaviour?: PropBehaviour;
  readonly footprint?: InstanceFootprint;
  readonly keepPlacement?: true;
}

/**
 * Returns the command that exactly reverses another.
 *
 * @param command Command to invert.
 * @returns The inverse command; inverting twice yields the original.
 */
export function invertCommand(command: Command): Command {
  if (isLayoutCommand(command)) {
    return invertLayoutCommand(command);
  }
  if (isDoorCommand(command)) {
    return invertDoorCommand(command);
  }
  switch (command.kind) {
    case "compound":
      return { kind: "compound", commands: [...command.commands].reverse().map(invertCommand) };
    case "changeTilesets":
      return {
        kind: "changeTilesets",
        remove: command.insert,
        insert: command.remove,
        ...(command.frames === undefined ? {} : { frames: command.frames.map((change) => ({ id: change.id, ...(change.after === undefined ? {} : { before: change.after }), ...(change.before === undefined ? {} : { after: change.before }) })) }),
      };
    case "setTerrainName":
      return { kind: "setTerrainName", entry: command.entry, ...optional("before", command.after), ...optional("after", command.before) };
    case "addMap":
      return { ...command, kind: "removeMap" };
    case "removeMap":
      return { ...command, kind: "addMap" };
    case "renameMap":
      return { ...command, before: command.after, after: command.before };
    case "paintCorners":
    case "paintCollision":
      return { ...command, cells: command.cells.map((c) => ({ index: c.index, before: c.after, after: c.before })) };
    case "setTiles":
      return { ...command, cells: command.cells.map((c) => ({ index: c.index, before: c.after, after: c.before })) };
    case "paintZone":
      return { ...command, cells: command.cells.map((c) => ({ index: c.index, before: c.after, after: c.before })) };
    case "addLayer":
      return { ...command, kind: "removeLayer" };
    case "removeLayer":
      return { ...command, kind: "addLayer" };
    case "moveLayer":
      return { ...command, from: command.to, to: command.from, ...(command.playersAbove === undefined ? {} : { playersAbove: { before: command.playersAbove.after, after: command.playersAbove.before } }) };
    case "addGroup":
      return { ...command, kind: "removeGroup" };
    case "removeGroup":
      return { ...command, kind: "addGroup" };
    case "fillCells":
      return { ...command, before: command.after, after: command.before };
    case "renameGroup":
    case "setLayerGroup":
    case "setPlayersAbove":
    case "setMapProperties":
    case "setIssueIgnores":
      return { ...command, before: command.after, after: command.before } as Command;
    case "addProp":
      return { ...command, kind: "removeProp" };
    case "removeProp":
      return { ...command, kind: "addProp" };
    case "addEntity":
      return { ...command, kind: "removeEntity" };
    case "removeEntity":
      return { ...command, kind: "addEntity" };
    case "renameLayer":
      return { ...command, before: command.after, after: command.before, ...(command.autoName === undefined ? {} : { autoName: { ...optional("before", command.autoName.after), ...optional("after", command.autoName.before) } }) };
    case "updateEntity":
    case "setLayerTerrain":
    case "updateProp":
      return { ...command, before: command.after, after: command.before } as Command;
    case "setLayerFaceHeight":
      return { kind: "setLayerFaceHeight", mapId: command.mapId, layerId: command.layerId, ...optional("before", command.after), ...optional("after", command.before) };
  }
}

/**
 * Checks a face height a command writes against what the document holds (ADR-072 decision 6); whether the
 * layer's terrain can draw it is the command builder's precondition, since the document knows no terrain.
 *
 * @param height The height, or undefined for none.
 * @param layerId The layer, for the message.
 * @throws Error when the height is not an integer from 1 to `FACE_HEIGHT_MAX`.
 */
function checkFaceHeight(height: number | undefined, layerId: string): void {
  if (height !== undefined && (!Number.isInteger(height) || height < 1 || height > FACE_HEIGHT_MAX)) {
    throw new Error(`layer ${layerId} face height ${height} must be an integer from 1 to ${FACE_HEIGHT_MAX}`);
  }
}

/**
 * An optional field as an object to spread: the key with its value, or nothing when the value is absent.
 *
 * @param key Field name.
 * @param value Its value, or undefined.
 * @returns `{ [key]: value }` or `{}`.
 */
function optional<K extends string, V>(key: K, value: V | undefined): { [P in K]?: V } {
  return (value === undefined ? {} : { [key]: value }) as { [P in K]?: V };
}

/**
 * The author's names for terrain entries with one entry set or, when the name is absent, removed; the field is
 * dropped from the world when no name is left.
 *
 * @param world The document.
 * @param entry The entry key.
 * @param before The name the entry must hold now, or undefined for none.
 * @param after The name to give it, or undefined to remove it.
 * @returns The new document.
 * @throws Error when the entry does not hold `before`.
 */
function setTerrainName(world: WorldDocument, entry: string, before: string | undefined, after: string | undefined): WorldDocument {
  const names = world.terrainNames ?? {};
  if (names[entry] !== before) {
    throw new Error(`terrain entry ${entry} does not match the recorded before name`);
  }
  const rest = Object.fromEntries(Object.entries(names).filter(([key]) => key !== entry));
  const next = after === undefined ? rest : { ...rest, [entry]: after };
  const { terrainNames: _previous, ...others } = world;
  return Object.keys(next).length === 0 ? others : { ...others, terrainNames: next };
}

/**
 * The Players band after a layer is added or removed (ADR-047): a pinned band moves so the same layers
 * stay ground (a layer added at the band's own index joins the band); a map that never pinned it keeps
 * deriving it from its layers.
 *
 * @param map The map before the command.
 * @param adjust The band after the command, given the band before.
 * @returns The `playersAbove` field to spread, or nothing.
 */
function bandAfter(map: MapDocument, adjust: (band: number) => number): { playersAbove?: number } {
  return map.playersAbove === undefined ? {} : { playersAbove: adjust(map.playersAbove) };
}

/**
 * Applies a command to a document.
 *
 * @param world Current document; never mutated.
 * @param command Command to apply.
 * @returns A new document with the command applied.
 * @throws Error when the target map or layer is missing, an index is out of range, or a recorded precondition
 *   does not match the document.
 */
export function applyCommand(world: WorldDocument, command: Command): WorldDocument {
  if (command.kind === "compound") {
    let current = world;
    for (let i = 0; i < command.commands.length; ) {
      const end = updatePropRunEnd(command.commands, i);
      const part = command.commands[i] as Command;
      current = end - i > 1 ? applyPropRun(current, command.commands.slice(i, end) as UpdatePropCommand[]) : applyCommand(current, part);
      i = end;
    }
    return current;
  }
  if (command.kind === "changeTilesets") {
    return changeTilesets(world, command.remove, command.insert, command.frames);
  }
  if (command.kind === "setTerrainName") {
    return setTerrainName(world, command.entry, command.before, command.after);
  }
  if (isLayoutCommand(command)) {
    return applyLayoutCommand(world, command);
  }
  if (command.kind === "addMap") {
    const added = addMapTo(world, command.index, command.map);
    validateDocument(added);
    return added;
  }
  if (command.kind === "removeMap") {
    return removeMapFrom(world, command.index, command.map);
  }
  if (command.kind === "renameMap") {
    return renameMapIn(world, command.mapId, command.before, command.after);
  }
  const mapIndex = world.maps.findIndex((m) => m.id === command.mapId);
  const map = world.maps[mapIndex];
  if (map === undefined) {
    throw new Error(`map ${command.mapId} does not exist`);
  }
  const updated = isDoorCommand(command) ? applyDoorCommand(map, command) : applyToMap(map, command, world.tilesets);
  return { ...world, maps: world.maps.map((m, i) => (i === mapIndex ? updated : m)) };
}

/**
 * Applies a command to the map it targets.
 *
 * @param map Target map.
 * @param command Command to apply.
 * @param tilesets The world's tilesets, for packing tile strokes.
 * @returns The updated map.
 */
function applyToMap(map: MapDocument, command: Exclude<Command, { readonly kind: "changeTilesets" | "compound" | "setTerrainName" | "addMap" | "removeMap" | "renameMap" | LayoutCommand["kind"] | DoorCommand["kind"] }>, tilesets: readonly TilesetRef[]): MapDocument {
  switch (command.kind) {
    case "paintCorners":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "terrain") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not terrain`);
        }
        return { ...layer, corners: applyCells(layer.corners, command.cells, (map.width + 1) * (map.height + 1), Uint8Array) };
      });
    case "paintCollision":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "collision") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not collision`);
        }
        return { ...layer, cells: applyCells(layer.cells, command.cells, map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE, Uint8Array) };
      });
    case "paintZone":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "zone") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not zone`);
        }
        return editZoneCells(layer, command.cells);
      });
    case "setTiles":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "tile") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not tile`);
        }
        const packed = command.cells.map((c) => ({ index: c.index, before: tileRefToPacked(c.before, tilesets), after: tileRefToPacked(c.after, tilesets) }));
        return { ...layer, tiles: applyCells(layer.tiles, packed, map.width * map.height, Uint32Array) };
      });
    case "fillCells":
      return replaceLayer(map, command.layerId, (layer) => fillLayer(layer, map, command, tilesets));
    case "addLayer": {
      assertInsertIndex(command.index, map.layers.length, "layer");
      if (map.layers.some((l) => l.id === command.layer.id)) {
        throw new Error(`layer ${command.layer.id} already exists`);
      }
      assertGridSize(command.layer, map);
      const withGroup = command.group === undefined ? map : addGroup(map, command.group);
      if (command.layer.group !== undefined && !(withGroup.groups ?? []).some((g) => g.id === command.layer.group)) {
        throw new Error(`layer ${command.layer.id} names group ${command.layer.group}, which the map lacks`);
      }
      const layers = insertAt(withGroup.layers, command.index, command.layer);
      assertUniqueDoorIds({ layers }, `map ${map.id}`);
      return { ...withGroup, ...bandAfter(map, (band) => (command.index < band ? band + 1 : band)), layers };
    }
    case "removeLayer": {
      const existing = map.layers[command.index];
      if (existing === undefined || !sameJson(existing, command.layer)) {
        throw new Error(`layer at ${command.index} does not match the carried layer`);
      }
      const without = { ...map, ...bandAfter(map, (band) => (command.index < band ? band - 1 : band)), layers: removeAt(map.layers, command.index) };
      return command.group === undefined ? without : removeGroup(without, command.group);
    }
    case "addGroup":
      return addGroup(map, command.group);
    case "removeGroup":
      return removeGroup(map, command.group);
    case "renameGroup": {
      const groups = map.groups ?? [];
      const group = groups.find((g) => g.id === command.groupId);
      if (group === undefined || group.name !== command.before) {
        throw new Error(`group ${command.groupId} does not match the recorded before name`);
      }
      return { ...map, groups: groups.map((g) => (g.id === command.groupId ? { ...g, name: command.after } : g)) };
    }
    case "setLayerGroup":
      if (command.after !== undefined && !(map.groups ?? []).some((g) => g.id === command.after)) {
        throw new Error(`group ${command.after} is not in the map`);
      }
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.group !== command.before) {
          throw new Error(`layer ${command.layerId} does not match the recorded before group`);
        }
        const { group: _dropped, ...rest } = layer;
        return (command.after === undefined ? rest : { ...rest, group: command.after }) as LayerDocument;
      });
    case "moveLayer": {
      assertIndex(command.from, map.layers.length, "from");
      assertIndex(command.to, map.layers.length, "to");
      const layer = map.layers[command.from] as LayerDocument;
      if (command.layerId !== undefined && layer.id !== command.layerId) {
        throw new Error(`layer at ${command.from} is ${layer.id}, not the carried ${command.layerId}`);
      }
      const band = command.playersAbove;
      if (band !== undefined) {
        if (playersAbove(map) !== band.before) {
          throw new Error(`playersAbove is ${playersAbove(map)}, not the carried ${band.before}`);
        }
        if (!Number.isInteger(band.after) || band.after < 0 || band.after > map.layers.length) {
          throw new Error(`playersAbove ${band.after} must lie within 0 to ${map.layers.length}`);
        }
      }
      return { ...map, ...(band === undefined ? {} : { playersAbove: band.after }), layers: insertAt(removeAt(map.layers, command.from), command.to, layer) };
    }
    case "setPlayersAbove": {
      if (playersAbove(map) !== command.before) {
        throw new Error(`playersAbove is ${playersAbove(map)}, not the carried ${command.before}`);
      }
      if (!Number.isInteger(command.after) || command.after < 0 || command.after > map.layers.length) {
        throw new Error(`playersAbove ${command.after} must lie within 0 to ${map.layers.length}`);
      }
      return { ...map, playersAbove: command.after };
    }
    case "setMapProperties":
      if (!sameJson(map.properties, command.before)) {
        throw new Error(`map ${map.id} properties do not match the carried record`);
      }
      return { ...map, properties: command.after };
    case "setIssueIgnores": {
      if (!sameJson(map.ignoredIssues ?? {}, command.before)) {
        throw new Error(`map ${map.id} ignored issues do not match the carried record`);
      }
      const { ignoredIssues: _dropped, ...rest } = map;
      const after = readIssueIgnores(command.after, `maps.${map.id}.ignoredIssues`);
      const count = Object.keys(after).length;
      if (count > IGNORED_ISSUES_MAX && count > Object.keys(command.before).length) {
        throw new Error(`maps.${map.id}.ignoredIssues would hold ${count} ignored issues, more than ${IGNORED_ISSUES_MAX}`);
      }
      return count === 0 ? rest : { ...rest, ignoredIssues: after };
    }
    case "addProp":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "prop") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not prop`);
        }
        assertInsertIndex(command.index, layer.props.length, "prop");
        assertFramesListed(command.prop, tilesets);
        assertInstanceFootprint(command.prop, command.prop.id);
        return { ...layer, props: insertAt(layer.props, command.index, command.prop) };
      });
    case "removeProp":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "prop") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not prop`);
        }
        const existing = layer.props[command.index];
        if (existing === undefined || !sameJson(existing, command.prop)) {
          throw new Error(`prop at ${command.index} does not match the carried prop`);
        }
        return { ...layer, props: removeAt(layer.props, command.index) };
      });
    case "addEntity":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "entity") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not entity`);
        }
        assertInsertIndex(command.index, layer.entities.length, "entity");
        return { ...layer, entities: insertAt(layer.entities, command.index, command.entity) };
      });
    case "removeEntity":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "entity") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not entity`);
        }
        const existing = layer.entities[command.index];
        if (existing === undefined || !sameJson(existing, command.entity)) {
          throw new Error(`entity at ${command.index} does not match the carried entity`);
        }
        return { ...layer, entities: removeAt(layer.entities, command.index) };
      });
    case "updateEntity":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "entity") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not entity`);
        }
        const index = layer.entities.findIndex((e) => e.id === command.entityId);
        const existing = layer.entities[index];
        if (existing === undefined) {
          throw new Error(`entity ${command.entityId} does not exist in layer ${command.layerId}`);
        }
        const current: EntityState = { x: existing.x, y: existing.y, fields: existing.fields };
        if (!sameJson(current, command.before)) {
          throw new Error(`entity ${command.entityId} does not match the recorded before state`);
        }
        const replaced: EntityInstance = { id: existing.id, type: existing.type, x: command.after.x, y: command.after.y, fields: command.after.fields };
        return { ...layer, entities: layer.entities.map((e, i) => (i === index ? replaced : e)) };
      });
    case "renameLayer":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.name !== command.before) {
          throw new Error(`layer ${command.layerId} does not match the recorded before name`);
        }
        if (command.autoName === undefined) {
          return { ...layer, name: command.after };
        }
        if (layer.kind !== "terrain" || layer.autoName !== command.autoName.before) {
          throw new Error(`layer ${command.layerId} does not match the recorded before autoName`);
        }
        const { autoName: _previous, ...rest } = layer;
        return { ...rest, name: command.after, ...optional("autoName", command.autoName.after) };
      });
    case "setLayerTerrain":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "terrain") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not terrain`);
        }
        if (layer.terrain !== command.before.terrain || layer.variant !== command.before.variant || (command.before.name !== undefined && layer.name !== command.before.name) || layer.faceHeight !== command.before.faceHeight) {
          throw new Error(`layer ${command.layerId} does not match the recorded before terrain`);
        }
        checkFaceHeight(command.after.faceHeight, command.layerId);
        const { faceHeight: _previous, ...rest } = layer;
        return { ...rest, terrain: command.after.terrain, variant: command.after.variant, name: command.after.name ?? layer.name, ...optional("faceHeight", command.after.faceHeight) };
      });
    case "setLayerFaceHeight":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "terrain") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not terrain`);
        }
        if (layer.faceHeight !== command.before) {
          throw new Error(`layer ${command.layerId} does not match the recorded before face height`);
        }
        checkFaceHeight(command.after, command.layerId);
        const { faceHeight: _previous, ...rest } = layer;
        return { ...rest, ...optional("faceHeight", command.after) };
      });
    case "updateProp":
      return replaceLayer(map, command.layerId, (layer) => {
        if (layer.kind !== "prop") {
          throw new Error(`layer ${command.layerId} is ${layer.kind}, not prop`);
        }
        const index = layer.props.findIndex((p) => p.id === command.propId);
        const replaced = updatedProp(layer.props[index], command, tilesets);
        return { ...layer, props: layer.props.map((p, i) => (i === index ? replaced : p)) };
      });
  }
}

/**
 * A command that changes one prop's state.
 */
export type UpdatePropCommand = Extract<Command, { readonly kind: "updateProp" }>;

/**
 * The prop an `updateProp` leaves, after the checks of the command.
 *
 * @param existing The prop as the layer holds it now, or undefined when the layer has none with the id.
 * @param command The command.
 * @param tilesets The world's tilesets.
 * @returns The replacement prop.
 * @throws Error when the prop is missing, differs from the recorded before state, names a tileset the world
 *   lacks, has a sort offset that is not an integer, or carries a footprint that is invalid or kept across a
 *   change of frame.
 */
function updatedProp(existing: PropInstance | undefined, command: UpdatePropCommand, tilesets: readonly TilesetRef[]): PropInstance {
  if (existing === undefined) {
    throw new Error(`prop ${command.propId} does not exist in layer ${command.layerId}`);
  }
  if (!sameJson(propState(existing), command.before)) {
    throw new Error(`prop ${command.propId} does not match the recorded before state`);
  }
  assertFramesListed(command.after, tilesets);
  if (!Number.isInteger(command.after.sortPixels)) {
    throw new Error(`prop ${command.propId} sort offset must be an integer`);
  }
  assertInstanceFootprint(command.after, command.propId);
  const behaviour = command.after.behaviour;
  const footprint = command.after.footprint;
  if (footprint !== undefined && sameJson(command.before.footprint, footprint) && !sameJson(command.before.frame, command.after.frame)) {
    throw new Error(`prop ${command.propId} changes frame, so it cannot keep its own footprint, whose cells are of the frame it had`);
  }
  return { id: existing.id, frame: command.after.frame, x: command.after.x, y: command.after.y, flipX: command.after.flipX, flipY: command.after.flipY, sortPixels: command.after.sortPixels, ...(behaviour === undefined ? {} : { behaviour }), ...(command.after.keepPlacement === true ? { keepPlacement: true } : {}), ...(footprint === undefined ? {} : { footprint }) };
}

/**
 * Where the run of `updateProp` parts that starts at an index ends: the parts after it that target the same
 * map and layer.
 *
 * @param parts The parts of a compound.
 * @param start Index of the first part of the run.
 * @returns The index after the run's last part; `start + 1` when the part at `start` is alone or not an
 *   `updateProp`.
 */
export function updatePropRunEnd(parts: readonly Command[], start: number): number {
  const first = parts[start];
  if (first === undefined || first.kind !== "updateProp") {
    return start + 1;
  }
  let end = start + 1;
  for (let part = parts[end]; part !== undefined && part.kind === "updateProp" && part.mapId === first.mapId && part.layerId === first.layerId; part = parts[end]) {
    end += 1;
  }
  return end;
}

/**
 * Applies `updateProp` commands of one map and layer in one pass over the layer, with the result, the order of
 * the checks and the errors of applying them one at a time: each part is checked against the prop as the parts
 * before it left it.
 *
 * @param world Current document; never mutated.
 * @param parts The commands, all `updateProp` on one map and layer.
 * @returns A new document with every part applied.
 * @throws Error as `applyCommand` does for the first part that does not apply.
 */
export function applyPropRun(world: WorldDocument, parts: readonly UpdatePropCommand[]): WorldDocument {
  const first = parts[0];
  if (first === undefined) {
    return world;
  }
  const mapIndex = world.maps.findIndex((m) => m.id === first.mapId);
  const map = world.maps[mapIndex];
  if (map === undefined) {
    throw new Error(`map ${first.mapId} does not exist`);
  }
  const updated = replaceLayer(map, first.layerId, (layer) => {
    if (layer.kind !== "prop") {
      throw new Error(`layer ${first.layerId} is ${layer.kind}, not prop`);
    }
    const props = layer.props.slice();
    const indexOf = new Map<string, number>();
    props.forEach((p, i) => {
      if (!indexOf.has(p.id)) {
        indexOf.set(p.id, i);
      }
    });
    for (const part of parts) {
      const index = indexOf.get(part.propId);
      const replaced = updatedProp(index === undefined ? undefined : props[index], part, world.tilesets);
      props[index as number] = replaced;
    }
    return { ...layer, props };
  });
  return { ...world, maps: world.maps.map((m, i) => (i === mapIndex ? updated : m)) };
}

/**
 * Checks that every frame a prop draws is on a tileset the world lists: its own frame, each state frame and
 * each transition animation, so neither placing nor changing a prop can name a tileset the document lacks
 * (ADR-037 amendment).
 *
 * @param prop The prop, or the state `updateProp` gives it.
 * @param tilesets The world's tilesets.
 * @throws Error naming the first tileset the world does not list.
 */
function assertFramesListed(prop: { readonly frame: TileRef; readonly behaviour?: PropBehaviour }, tilesets: readonly TilesetRef[]): void {
  for (const frame of [prop.frame, ...Object.values(prop.behaviour?.frames ?? {}), ...Object.values(prop.behaviour?.animations ?? {})]) {
    tileRefToPacked(frame, tilesets);
  }
}

/**
 * The part of a prop `updateProp` changes.
 *
 * @param prop The prop.
 * @returns Its state.
 */
export function propState(prop: PropInstance): PropState {
  return { frame: prop.frame, x: prop.x, y: prop.y, flipX: prop.flipX, flipY: prop.flipY, sortPixels: prop.sortPixels, ...(prop.behaviour === undefined ? {} : { behaviour: prop.behaviour }), ...(prop.keepPlacement === true ? { keepPlacement: true as const } : {}), ...(prop.footprint === undefined ? {} : { footprint: prop.footprint }) };
}

/**
 * Replaces one layer of a map by id.
 *
 * @param map Map holding the layer.
 * @param layerId Layer to replace.
 * @param update Produces the replacement from the current layer.
 * @returns The updated map.
 * @throws Error when the layer does not exist.
 */
/**
 * Adds a group to a map.
 *
 * @param map The map.
 * @param group The group.
 * @returns The map with the group appended.
 * @throws Error when a group with that id exists.
 */
function addGroup(map: MapDocument, group: LayerGroup): MapDocument {
  const groups = map.groups ?? [];
  if (groups.some((g) => g.id === group.id)) {
    throw new Error(`group ${group.id} already exists`);
  }
  return { ...map, groups: [...groups, { id: group.id, name: group.name }] };
}

/**
 * Removes an empty group from a map; a map with no groups left drops the field.
 *
 * @param map The map.
 * @param group The group as recorded, for the inverse to restore it.
 * @returns The map without the group.
 * @throws Error when the group is missing, differs from the recorded one, or still holds layers.
 */
function removeGroup(map: MapDocument, group: LayerGroup): MapDocument {
  const groups = map.groups ?? [];
  const existing = groups.find((g) => g.id === group.id);
  if (existing === undefined || existing.name !== group.name) {
    throw new Error(`group ${group.id} does not match the carried group`);
  }
  if (map.layers.some((l) => l.group === group.id)) {
    throw new Error(`group ${group.id} still holds layers`);
  }
  const remaining = groups.filter((g) => g.id !== group.id);
  const { groups: _dropped, ...rest } = map;
  return remaining.length === 0 ? rest : { ...rest, groups: remaining };
}

function replaceLayer(map: MapDocument, layerId: string, update: (layer: LayerDocument) => LayerDocument): MapDocument {
  const index = map.layers.findIndex((l) => l.id === layerId);
  const layer = map.layers[index];
  if (layer === undefined) {
    throw new Error(`layer ${layerId} does not exist in map ${map.id}`);
  }
  const replaced = update(layer);
  return { ...map, layers: map.layers.map((l, i) => (i === index ? replaced : l)) };
}

/**
 * Applies cell changes to a copy of a grid after checking every `before` value, then checks that every
 * `after` value was stored exactly, which rejects out-of-range values and contradictory duplicate cells.
 *
 * @param grid Current grid.
 * @param cells Changes to apply.
 * @param length Expected grid length.
 * @param Ctor Typed array constructor for the copy.
 * @returns A new grid with the changes applied.
 * @throws Error when an index is out of range, a `before` value does not match, or an `after` value cannot be
 *   stored as given.
 */
function applyCells<T extends Uint8Array | Uint32Array>(grid: T, cells: readonly CellChange[], length: number, Ctor: new (source: T) => T): T {
  for (const cell of cells) {
    if (!Number.isInteger(cell.index) || cell.index < 0 || cell.index >= length) {
      throw new Error(`cell index ${cell.index} is outside the grid of ${length}`);
    }
    if (grid[cell.index] !== cell.before) {
      throw new Error(`cell ${cell.index} holds ${grid[cell.index]}, not the recorded before value ${cell.before}`);
    }
  }
  const copy = new Ctor(grid);
  for (const cell of cells) {
    copy[cell.index] = cell.after;
  }
  for (const cell of cells) {
    if (copy[cell.index] !== cell.after) {
      throw new Error(`cell ${cell.index} cannot hold ${cell.after}; the stroke would not be undoable`);
    }
  }
  return copy;
}

/**
 * Applies a `fillCells` region to its layer: every index must hold the before value, and gets the after
 * value, in one copy of the layer's grid.
 *
 * @param layer The layer.
 * @param map The map, for the grid's size.
 * @param command The fill.
 * @param tilesets The world's tilesets, which pack a tile.
 * @returns The layer filled.
 * @throws Error when the layer has no grid, a value is not of its kind, an index is outside the grid or a
 *   cell does not hold the before value.
 */
function fillLayer(layer: LayerDocument, map: MapDocument, command: Extract<Command, { readonly kind: "fillCells" }>, tilesets: readonly TilesetRef[]): LayerDocument {
  if (layer.kind === "terrain" || layer.kind === "collision") {
    const before = fillByte(command.before, layer.kind);
    const after = fillByte(command.after, layer.kind);
    const grid = layer.kind === "terrain" ? layer.corners : layer.cells;
    const length = layer.kind === "terrain" ? (map.width + 1) * (map.height + 1) : map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;
    const filled = fillGrid(grid, command.indices, before, after, length, Uint8Array);
    return layer.kind === "terrain" ? { ...layer, corners: filled } : { ...layer, cells: filled };
  }
  if (layer.kind === "zone") {
    return fillZoneCells(layer, command.indices, command.before, command.after);
  }
  if (layer.kind === "door") {
    return fillDoorCells(layer, command.indices, command.before, command.after);
  }
  if (layer.kind === "tile") {
    if (typeof command.before === "number" || typeof command.after === "number" || typeof command.before === "string" || typeof command.after === "string") {
      throw new Error(`a tile fill writes tiles, not ${JSON.stringify(typeof command.before === "number" ? command.before : command.after)}`);
    }
    return { ...layer, tiles: fillGrid(layer.tiles, command.indices, tileRefToPacked(command.before, tilesets), tileRefToPacked(command.after, tilesets), map.width * map.height, Uint32Array) };
  }
  throw new Error(`layer ${layer.id} is a ${layer.kind} layer, which holds no cells to fill`);
}

/**
 * A fill value a byte grid holds.
 *
 * @param value The value.
 * @param kind The layer's kind, for the message.
 * @returns The byte.
 * @throws Error when the value is not an integer from 0 to 255.
 */
function fillByte(value: FillCellValue, kind: string): number {
  if (typeof value !== "number" || !Number.isInteger(value) || value < 0 || value > 255) {
    throw new Error(`a ${kind} fill writes a byte, not ${JSON.stringify(value)}`);
  }
  return value;
}

/**
 * Copies a grid with every listed index moved from one value to another, checking each first.
 *
 * @param grid The grid.
 * @param indices The indices.
 * @param before The value each must hold.
 * @param after The value each gets.
 * @param length The grid's length.
 * @param Ctor The grid's constructor.
 * @returns The copy.
 * @throws Error when an index is outside the grid or does not hold `before`.
 */
function fillGrid<T extends Uint8Array | Uint32Array>(grid: T, indices: Readonly<Uint32Array>, before: number, after: number, length: number, Ctor: new (source: T) => T): T {
  for (let i = 0; i < indices.length; i += 1) {
    const index = indices[i] as number;
    if (index >= length) {
      throw new Error(`fill index ${index} is outside the grid of ${length}`);
    }
    if (grid[index] !== before) {
      throw new Error(`cell ${index} holds ${grid[index]}, not the recorded before value ${before}`);
    }
  }
  const copy = new Ctor(grid);
  for (let i = 0; i < indices.length; i += 1) {
    copy[indices[i] as number] = after;
  }
  return copy;
}

/**
 * Checks that a layer's grid, if it has one, matches the map dimensions.
 *
 * @param layer Layer being added.
 * @param map Map it is added to.
 * @throws Error when the grid length is wrong.
 */
function assertGridSize(layer: LayerDocument, map: MapDocument): void {
  if (layer.kind === "terrain") {
    const expected = (map.width + 1) * (map.height + 1);
    if (layer.corners.length !== expected) {
      throw new Error(`terrain layer ${layer.id} has ${layer.corners.length} corners, expected ${expected}`);
    }
  } else if (layer.kind === "tile") {
    const expected = map.width * map.height;
    if (layer.tiles.length !== expected) {
      throw new Error(`tile layer ${layer.id} has ${layer.tiles.length} tiles, expected ${expected}`);
    }
  } else if (layer.kind === "zone" || layer.kind === "door") {
    const expected = map.width * map.height * COLLISION_CELLS_PER_TILE * COLLISION_CELLS_PER_TILE;
    if (layer.cells.length !== expected) {
      throw new Error(`${layer.kind} layer ${layer.id} has ${layer.cells.length} cells, expected ${expected}`);
    }
  }
}

/**
 * Checks an index refers to an existing element.
 *
 * @param index Candidate index.
 * @param length Array length.
 * @param label Name for the error.
 * @throws Error when out of range.
 */
function assertIndex(index: number, length: number, label: string): void {
  if (!Number.isInteger(index) || index < 0 || index >= length) {
    throw new Error(`${label} index ${index} is outside [0, ${length})`);
  }
}

/**
 * Checks an index is a valid insertion point, which may equal the length.
 *
 * @param index Candidate index.
 * @param length Array length.
 * @param label Name for the error.
 * @throws Error when out of range.
 */
function assertInsertIndex(index: number, length: number, label: string): void {
  if (!Number.isInteger(index) || index < 0 || index > length) {
    throw new Error(`${label} insert index ${index} is outside [0, ${length}]`);
  }
}

/**
 * Returns a copy of an array with an item inserted.
 *
 * @param items Source array.
 * @param index Insertion index.
 * @param item Item to insert.
 * @returns The new array.
 */
function insertAt<T>(items: readonly T[], index: number, item: T): T[] {
  return [...items.slice(0, index), item, ...items.slice(index)];
}

/**
 * Returns a copy of an array with an item removed.
 *
 * @param items Source array.
 * @param index Index to remove.
 * @returns The new array.
 */
function removeAt<T>(items: readonly T[], index: number): T[] {
  return [...items.slice(0, index), ...items.slice(index + 1)];
}
